
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { showLoader, hideLoader } from "./loaderSlice";

export const getSpecialityList = createAsyncThunk(
  "getSpecialityList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(`/getall/clinic/roles`);

      if (res.status === 200) {
        const specialities = res.data.data.filter(
          (role) => role.type === "speciality"
        );
        thunkAPI.dispatch(hideLoader());
        return specialities;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch specialities");
    }
  }
);

export const createSpeciality = createAsyncThunk(
  "createSpeciality",
  async ({ roleName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/role?type=speciality&roleName=${roleName}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create speciality");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateSpeciality = createAsyncThunk(
  "updateSpeciality",
  async ({ roleId, roleName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/role/${roleId}?type=speciality&roleName=${roleName}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update speciality");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteSpeciality = createAsyncThunk(
  "deleteSpeciality",
  async ({ roleId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/role/${roleId}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return roleId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete speciality");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const specialitySlice = createSlice({
  name: "speciality",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSpecialityList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createSpeciality.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateSpeciality.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex((s) => s.roleId === updated.roleId);
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteSpeciality.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((s) => s.roleId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getSpecialityList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSpeciality.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSpeciality.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSpeciality.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSpecialityList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching specialities.";
      });
  },
});

export default specialitySlice.reducer;